import React from "react";
import { VELLUM as T, SANS, display, mono, body } from "./kit";

/* Form apparatus in the folio vocabulary.

   A form is a schedule of values waiting to be filled in, which is what a
   drawing's title block already is: a mono label, a hairline, and a cell for
   the value. So the fields are drawn as one - the same apparatus the sheet
   uses for its live figures, rather than a second, unrelated form language.

   The block is solid ground on a ruled sheet. A real title block is a clean
   box drawn over the graph paper, and it keeps the values off the ruling
   where they would otherwise be read against it. */

export const FP = "ff";

/* Heading and sheet code share a baseline. They are never stacked: a label
   sitting above a heading is a kicker, and the heading carries its own
   weight without one. */
export const SheetTitle: React.FC<{
  id?: string;
  code: string;
  title: string;
  standfirst?: string;
}> = ({ id, code, title, standfirst }) => (
  <>
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
      <h2 id={id} style={display(T, "clamp(28px, 4.6vw, 36px)")}>
        {title}
      </h2>
      <span style={mono(T, T.accent)}>{code}</span>
    </div>
    {standfirst && (
      <p style={{ ...body(T), fontSize: 14.5, marginTop: 12, maxWidth: "46ch" }}>{standfirst}</p>
    )}
  </>
);

/* The bordered block the rows are ruled into. */
export const FieldBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ border: `1.5px solid ${T.ink}`, background: T.ground }}>{children}</div>
);

export const FieldRow: React.FC<{
  label: string;
  htmlFor: string;
  required?: boolean;
  first?: boolean;
  error?: boolean;
  /* For a control the label column cannot sit beside - a textarea, mainly.
     The label takes its own line above and the cell keeps the ruling. */
  stacked?: boolean;
  children: React.ReactNode;
}> = ({ label, htmlFor, required, first, error, stacked, children }) => (
  <div
    className={`${FP}-field${error ? ` ${FP}-field-err` : ""}`}
    style={{
      display: "grid",
      gridTemplateColumns: stacked ? "minmax(0,1fr)" : "118px minmax(0,1fr)",
      alignItems: "stretch",
      borderTop: first ? "none" : `1px solid ${error ? T.accent : T.rule}`,
    }}
  >
    <label
      htmlFor={htmlFor}
      className={`${FP}-field-label`}
      style={{
        ...mono(T, error ? T.accent : T.mute),
        display: "flex",
        alignItems: "center",
        gap: 3,
        padding: stacked ? "12px 14px 0" : "0 12px",
        minHeight: stacked ? 0 : 46,
        borderRight: stacked ? "none" : `1px solid ${error ? T.accent : T.rule}`,
        cursor: "pointer",
      }}
    >
      {label}
      {required && <span style={{ color: T.accent }}>*</span>}
    </label>
    <div style={{ display: stacked ? "block" : "flex", alignItems: "center", minWidth: 0 }}>{children}</div>
  </div>
);

/* Sits inside a FieldRow's value cell. The cell draws the structure, so the
   control itself carries none. */
export const fieldInput: React.CSSProperties = {
  width: "100%",
  minWidth: 0,
  background: "transparent",
  border: "none",
  outline: "none",
  padding: "13px 14px",
  fontFamily: SANS,
  fontSize: 15.5,
  color: T.ink,
};

/* For a stacked row. Resizes vertically only, so it can never break the
   sheet's measure. */
export const fieldArea: React.CSSProperties = {
  ...fieldInput,
  display: "block",
  padding: "8px 14px 14px",
  lineHeight: 1.55,
  resize: "vertical",
};

export const CheckRow: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  required?: boolean;
  error?: boolean;
  children: React.ReactNode;
}> = ({ checked, onChange, required, error, children }) => (
  <label
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 11,
      fontFamily: SANS,
      fontSize: 13,
      lineHeight: 1.45,
      color: error ? T.accent : T.ink,
      cursor: "pointer",
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className={`${FP}-check`}
      style={{
        appearance: "none",
        WebkitAppearance: "none",
        width: 16,
        height: 16,
        margin: 0,
        borderRadius: 0,
        border: `1.5px solid ${error ? T.accent : T.ink}`,
        background: checked ? T.ink : "transparent",
        flexShrink: 0,
        marginTop: 1,
        cursor: "pointer",
        display: "inline-block",
        position: "relative",
      }}
    />
    <span>
      {children}
      {required && <span style={{ color: T.accent }}> *</span>}
    </span>
  </label>
);

/* Names the problem. The recovery is named by whatever raised it. */
export const ErrorNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    role="alert"
    style={{
      display: "grid",
      gridTemplateColumns: "auto minmax(0,1fr)",
      gap: 12,
      alignItems: "start",
      border: `1.5px solid ${T.accent}`,
      padding: "13px 15px",
    }}
  >
    <span style={{ ...mono(T, T.accent), paddingTop: 2 }}>!</span>
    <span style={{ fontFamily: SANS, fontSize: 14, lineHeight: 1.45, color: T.accent }}>{children}</span>
  </div>
);

/* A caret drawn rather than typed - a glyph standing in for an icon is a
   costume, and the select's own indicator is a different drawing on every
   platform. */
export const Caret: React.FC = () => (
  <svg aria-hidden width="9" height="6" viewBox="0 0 9 6" fill="none" style={{ marginRight: 14, flexShrink: 0 }}>
    <path d="M1 1L4.5 4.5L8 1" stroke={T.mute} strokeWidth="1.3" strokeLinecap="square" />
  </svg>
);

export const folioFormCss = `
  /* The theme's placeholder measures 3.02:1 against the ground and fails
     WCAG AA; mute measures 4.96:1. */
  .${FP}-field input::placeholder,
  .${FP}-field textarea::placeholder { color: ${T.mute}; opacity: 1; }

  /* The cell is the control, so the cell takes the focus. */
  .${FP}-field:focus-within { box-shadow: inset 0 0 0 1.5px ${T.accent}; }
  .${FP}-field:focus-within .${FP}-field-label { color: ${T.accent}; }
  .${FP}-field input:focus-visible,
  .${FP}-field textarea:focus-visible,
  .${FP}-field select:focus-visible { outline: none; }

  .${FP}-check:focus-visible { outline: 2px solid ${T.accent}; outline-offset: 2px; }
  .${FP}-check:checked::after {
    content: "";
    position: absolute;
    left: 4px; top: 1px;
    width: 4px; height: 8px;
    border: solid ${T.ground};
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(42deg);
  }

  .${FP}-btn { transition: background 150ms ease, color 150ms ease, border-color 150ms ease; }
  .${FP}-btn:hover:not(:disabled) { background: ${T.accentDeep}; }
  .${FP}-btn:disabled { opacity: 0.55; cursor: not-allowed; }
  .${FP}-quiet:hover { color: ${T.accent}; border-bottom-color: ${T.accent}; }
  .${FP}-link:hover { color: ${T.accentDeep}; }

  .${FP}-role:hover:not([aria-pressed="true"]) { background: rgba(10,110,126,0.06); }

  .${FP}-btn:focus-visible,
  .${FP}-quiet:focus-visible,
  .${FP}-link:focus-visible,
  .${FP}-role:focus-visible,
  .${FP}-close:focus-visible {
    outline: 2px solid ${T.accent};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    .${FP}-btn { transition: none; }
  }

  @media (max-width: 520px) {
    /* The label column stops earning its width; the rows stack. */
    .${FP}-field { grid-template-columns: minmax(0,1fr) !important; }
    .${FP}-field-label {
      min-height: 0 !important;
      border-right: none !important;
      padding: 11px 14px 0 !important;
    }
    .${FP}-field input,
    .${FP}-field select { padding-top: 6px !important; padding-bottom: 13px !important; }
    .${FP}-field textarea { padding-top: 6px !important; }
  }
`;
